import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  FileText,
  Clock,
  User,
  CheckCircle2,
  XCircle,
  Pause,
  RefreshCw,
  Search,
  ChevronRight,
  ChevronLeft,
  Inbox,
  AlertCircle,
  Sparkles,
  Plus,
  Settings,
  Trash2,
  Send,
  Loader2,
  Upload,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import MatterSearchUnavailableNotice from "../components/MatterSearchUnavailableNotice";
// Aliased: OutgoingPostDetail already has a local searchMatters callback, and an
// import that shadows or is shadowed by a local name is how you get silent
// recursion. The alias makes the call site unambiguous in every file.
import { searchMatters as searchMattersApi, MatterSearchUnavailable, MATTER_SEARCH_UNAVAILABLE_MESSAGE } from "../lib/matterSearch";
import { fmtUK } from "../lib/dateUtils";
import { toast } from "sonner";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-status-amber-dim text-status-amber border-amber-200", icon: Clock },
  claimed: { label: "Claimed", color: "bg-blue-50 text-blue-700 border-blue-200", icon: User },
  parked: { label: "Parked", color: "bg-slate-100 text-txt-muted border-navy-border", icon: Pause },
  approved: { label: "Approved", color: "bg-teal-dim text-teal border-teal", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-status-red-dim text-status-red border-red-200", icon: XCircle },
};

const OUTGOING_DOC_TYPES = [
  { value: "letter", label: "Letter" },
  { value: "report", label: "Report" },
  { value: "contract", label: "Contract" },
  { value: "deed", label: "Deed" },
  { value: "completion_statement", label: "Completion Statement" },
  { value: "other", label: "Other" },
];

export default function PostIntakeQueue() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("incoming");
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState({ pending: 0, claimed: 0, parked: 0 });
  const [syncing, setSyncing] = useState(false);

  // Manual creation dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newFilename, setNewFilename] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [creating, setCreating] = useState(false);

  // Config management
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [config, setConfig] = useState({ document_types: [], deadline_categories: [] });
  const [editDocTypes, setEditDocTypes] = useState([]);
  const [editCategories, setEditCategories] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);

  // To-Do list config
  const [todoLists, setTodoLists] = useState([]);
  const [selectedTodoListId, setSelectedTodoListId] = useState(null);
  const [loadingTodoLists, setLoadingTodoLists] = useState(false);

  // Notify Fee Earners
  const [notifying, setNotifying] = useState(false);
  const [showNotifyConfirm, setShowNotifyConfirm] = useState(false);
  const [notifySentBy, setNotifySentBy] = useState("");
  const [directors, setDirectors] = useState({});

  // Outgoing post state — manual upload
  const [outMatterSearch, setOutMatterSearch] = useState("");
  const [outMatterResults, setOutMatterResults] = useState([]);
  const [outMatterRef, setOutMatterRef] = useState("");
  const [outDocType, setOutDocType] = useState("");
  const [outDescription, setOutDescription] = useState("");
  const [outDateSent, setOutDateSent] = useState(new Date().toISOString().slice(0, 10));
  const [outFile, setOutFile] = useState(null);
  const [outUploading, setOutUploading] = useState(false);
  const [outRecentItems, setOutRecentItems] = useState([]);
  const [outLoadingRecent, setOutLoadingRecent] = useState(false);
  const [outSearching, setOutSearching] = useState(false);
  // BM-H285: an unreadable matter list gets its own state, so the UI can say
  // "we could not look" rather than showing an empty dropdown.
  const [outMatterError, setOutMatterError] = useState("");
  const fileInputRef = useRef(null);
  const matterSearchTimeout = useRef(null);

  // Outgoing post state — scanned items
  const [scannedItems, setScannedItems] = useState([]);
  const [scannedLoading, setScannedLoading] = useState(false);
  const [scannedCounts, setScannedCounts] = useState({ pending: 0, filed: 0 });
  const [outSyncing, setOutSyncing] = useState(false);
  const [showManualUpload, setShowManualUpload] = useState(false);
  const [assigningId, setAssigningId] = useState(null);
  const [assignMatterSearch, setAssignMatterSearch] = useState("");
  const [assignMatterResults, setAssignMatterResults] = useState([]);
  const [assignSearching, setAssignSearching] = useState(false);
  const [assignMatterError, setAssignMatterError] = useState("");
  const assignSearchTimeout = useRef(null);

  useEffect(() => {
    axios.get(`${API}/staff`).then(res => setDirectors(res.data.directors || {})).catch(() => {});
  }, []);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/postintake/queue${params}`);
      setQueue(response.data.items || []);
      setStats({
        pending: response.data.pending || 0,
        claimed: response.data.claimed || 0,
        parked: response.data.parked || 0,
      });
    } catch (error) {
      console.error("Failed to fetch queue:", error);
      toast.error("Failed to load post intake queue");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/postintake/config`);
      setConfig(response.data);
      setEditDocTypes(response.data.document_types || []);
      setEditCategories(response.data.deadline_categories || []);
    } catch (error) {
      console.error("Failed to fetch config:", error);
    }
  };

  useEffect(() => {
    fetchQueue();
    fetchConfig();
  }, [fetchQueue]);

  const fetchOutgoingRecent = useCallback(async () => {
    setOutLoadingRecent(true);
    try {
      const res = await axios.get(`${API}/postintake/outgoing?limit=20`);
      setOutRecentItems(res.data.items || []);
    } catch {
      // non-critical
    } finally {
      setOutLoadingRecent(false);
    }
  }, []);

  const fetchScannedOutgoing = useCallback(async () => {
    setScannedLoading(true);
    try {
      const res = await axios.get(`${API}/postintake/outgoing/scanned?limit=50`);
      setScannedItems(res.data.items || []);
      setScannedCounts({ pending: res.data.pending || 0, filed: res.data.filed || 0 });
    } catch {
      // non-critical
    } finally {
      setScannedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "outgoing") {
      fetchScannedOutgoing();
      fetchOutgoingRecent();
    }
  }, [activeTab, fetchScannedOutgoing, fetchOutgoingRecent]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/postintake/sync`);
      toast.success(`Synced ${response.data.new_documents} new documents`);
      fetchQueue();
    } catch (error) {
      console.error("Sync failed:", error);
      toast.error("Failed to sync from SharePoint");
    } finally {
      setSyncing(false);
    }
  };

  const handleManualCreate = async () => {
    if (!newFilename.trim()) {
      toast.error("Please enter a filename");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/postintake/queue/manual`, {
        filename: newFilename.trim(),
        notes: newNotes.trim() || null,
      });
      toast.success(`Document "${newFilename}" added to queue`);
      setNewFilename("");
      setNewNotes("");
      setShowCreateDialog(false);
      fetchQueue();
    } catch (error) {
      toast.error("Failed to create document");
    } finally {
      setCreating(false);
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${API}/postintake/config`, {
        document_types: editDocTypes,
        deadline_categories: editCategories,
      });
      // Save To-Do list preference if changed
      if (selectedTodoListId) {
        const selectedList = todoLists.find(l => l.id === selectedTodoListId);
        await axios.post(`${API}/postintake/config/todo-lists`, {
          list_id: selectedTodoListId,
          list_name: selectedList?.name || "",
        });
      }
      setConfig({ document_types: editDocTypes, deadline_categories: editCategories });
      toast.success("Configuration saved");
      setShowConfigDialog(false);
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSavingConfig(false);
    }
  };

  const fetchTodoLists = async () => {
    setLoadingTodoLists(true);
    try {
      const res = await axios.get(`${API}/postintake/config/todo-lists`);
      setTodoLists(res.data.lists || []);
      setSelectedTodoListId(res.data.selected_list_id || null);
    } catch {
      // MS auth may not be available — not critical
    } finally {
      setLoadingTodoLists(false);
    }
  };

  const handleNotifyFeeEarners = async () => {
    if (!notifySentBy) {
      toast.error("Please select who is sending this notification");
      return;
    }
    setNotifying(true);
    try {
      const res = await axios.post(`${API}/postintake/notify-fee-earners`, {
        triggered_by: notifySentBy,
      });
      const d = res.data;
      if (d.status === "no_items") {
        toast.info("No pending items to notify about");
      } else {
        const parts = [];
        if (d.fee_earners_notified > 0) parts.push(`${d.fee_earners_notified} fee earner${d.fee_earners_notified !== 1 ? "s" : ""} notified`);
        if (d.unassigned_count > 0) parts.push(`${d.unassigned_count} unassigned sent to client care`);
        toast.success(`Notifications sent: ${parts.join(", ")}`);
      }
      setShowNotifyConfirm(false);
      fetchQueue();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to send notifications"));
    } finally {
      setNotifying(false);
    }
  };

  const addDocType = () => {
    setEditDocTypes([...editDocTypes, { value: "", label: "" }]);
  };

  const addCategory = () => {
    setEditCategories([...editCategories, { value: "", label: "", color: "text-txt-muted" }]);
  };

  const formatDate = (dateStr) => fmtUK(dateStr, "dd MMM yyyy HH:mm");

  const getStatusBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = cfg.icon;
    return (
      <Badge variant="outline" className={`${cfg.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {cfg.label}
      </Badge>
    );
  };

  const filteredQueue = queue.filter(item => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.filename?.toLowerCase().includes(search) ||
      item.id?.toLowerCase().includes(search)
    );
  });

  // ── Outgoing Post Handlers ──────────────────────────────────────

  const handleMatterSearch = (query) => {
    setOutMatterSearch(query);
    setOutMatterRef("");
    if (matterSearchTimeout.current) clearTimeout(matterSearchTimeout.current);
    if (!query.trim()) {
      setOutMatterResults([]);
      return;
    }
    matterSearchTimeout.current = setTimeout(async () => {
      setOutSearching(true);
      try {
        // BM-H285: searchMatters throws rather than resolving empty when the
        // matter list could not be read, so a broken search can no longer
        // masquerade as a matter that does not exist.
        const results = await searchMattersApi(query);
        setOutMatterError("");
        setOutMatterResults(results);
      } catch (err) {
        setOutMatterResults([]);
        setOutMatterError(
          err instanceof MatterSearchUnavailable
            ? err.message
            : MATTER_SEARCH_UNAVAILABLE_MESSAGE
        );
        toast.error("Matter search unavailable. Do not file on an empty result.");
      } finally { setOutSearching(false); }
    }, 300);
  };

  const selectMatter = (folder) => {
    setOutMatterRef(folder.name);
    setOutMatterSearch(folder.name);
    setOutMatterResults([]);
  };

  const handleOutgoingSync = async () => {
    setOutSyncing(true);
    try {
      const res = await axios.post(`${API}/postintake/outgoing/sync`);
      toast.success(`Outgoing sync complete — ${res.data.pending} pending, ${res.data.filed} filed`);
      fetchScannedOutgoing();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to sync outgoing post"));
    } finally {
      setOutSyncing(false);
    }
  };

  const handleAssignMatterSearch = (query) => {
    setAssignMatterSearch(query);
    if (assignSearchTimeout.current) clearTimeout(assignSearchTimeout.current);
    if (!query.trim()) { setAssignMatterResults([]); return; }
    assignSearchTimeout.current = setTimeout(async () => {
      setAssignSearching(true);
      try {
        // BM-H285: searchMatters throws rather than resolving empty when the
        // matter list could not be read, so a broken search can no longer
        // masquerade as a matter that does not exist.
        const results = await searchMattersApi(query);
        setAssignMatterError("");
        setAssignMatterResults(results);
      } catch (err) {
        setAssignMatterResults([]);
        setAssignMatterError(
          err instanceof MatterSearchUnavailable
            ? err.message
            : MATTER_SEARCH_UNAVAILABLE_MESSAGE
        );
        toast.error("Matter search unavailable. Do not file on an empty result.");
      } finally { setAssignSearching(false); }
    }, 300);
  };

  const handleAssignToMatter = async (itemId, matterRef) => {
    try {
      await axios.post(`${API}/postintake/outgoing/${itemId}/assign`, {
        matter_ref: matterRef,
        assigned_by: "",
      });
      toast.success(`Filed to ${matterRef}`);
      setAssigningId(null);
      setAssignMatterSearch("");
      setAssignMatterResults([]);
      fetchScannedOutgoing();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to assign to matter"));
    }
  };

  const handleOutgoingSubmit = async () => {
    const matterRef = outMatterRef || outMatterSearch.trim();
    if (!matterRef) { toast.error("Please select or enter a matter reference"); return; }
    if (!outDocType) { toast.error("Please select a document type"); return; }
    if (!outFile) { toast.error("Please select a file to upload"); return; }

    setOutUploading(true);
    try {
      const formData = new FormData();
      formData.append("matter_ref", matterRef);
      formData.append("document_type", outDocType);
      formData.append("date_sent", outDateSent);
      formData.append("description", outDescription.trim());
      formData.append("file", outFile);
      formData.append("uploaded_by", "");

      const res = await axios.post(`${API}/postintake/outgoing`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.sharepoint_url) {
        toast.success("Document uploaded to SharePoint");
      } else {
        toast.success("Document recorded (SharePoint upload pending)");
      }

      // Reset form
      setOutMatterSearch("");
      setOutMatterRef("");
      setOutDocType("");
      setOutDescription("");
      setOutDateSent(new Date().toISOString().slice(0, 10));
      setOutFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchOutgoingRecent();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to upload document"));
    } finally {
      setOutUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <div className="glass-nav sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-bold text-teal flex items-center gap-2">
                  <Inbox className="w-6 h-6" />
                  Post Intake
                </h1>
                <p className="text-sm text-slate-500">Manage incoming and outgoing post</p>
              </div>
            </div>
            {activeTab === "incoming" && (
              <div className="flex items-center gap-2">
                {/* Notify Fee Earners Button */}
                <Dialog open={showNotifyConfirm} onOpenChange={setShowNotifyConfirm}>
                  <DialogTrigger asChild>
                    <Button
                      variant="default"
                      size="sm"
                      className="gap-1.5 bg-teal hover:bg-teal/90"
                      disabled={queue.filter(i => ["pending", "claimed", "parked"].includes(i.status)).length === 0}
                      data-testid="notify-fee-earners-btn"
                    >
                      <Send className="w-4 h-4" />
                      <span className="hidden sm:inline">Notify Fee Earners</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Notify Fee Earners</DialogTitle>
                      <DialogDescription>
                        Send email notifications to each fee earner with their pending post items. Unassigned items will be sent to client care.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="bg-navy-hover/60 rounded-md p-3 text-sm space-y-1">
                      <p><strong>{queue.filter(i => ["pending", "claimed", "parked"].includes(i.status)).length}</strong> pending items will be included</p>
                      <p className="text-slate-500">Each fee earner receives one summary email with direct review links</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-txt mb-1">Sent by <span className="text-status-red">*</span></label>
                      <select
                        value={notifySentBy}
                        onChange={e => setNotifySentBy(e.target.value)}
                        className="w-full h-9 rounded-md border border-navy-border text-sm px-3 bg-white"
                        data-testid="notify-sent-by"
                      >
                        <option value="">Select who is sending</option>
                        {Object.entries(directors).map(([email, name]) => (
                          <option key={email} value={email}>{name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex justify-end gap-2 mt-2">
                      <Button variant="outline" size="sm" onClick={() => setShowNotifyConfirm(false)}>Cancel</Button>
                      <Button
                        size="sm"
                        className="bg-teal hover:bg-teal/90 gap-1.5"
                        onClick={handleNotifyFeeEarners}
                        disabled={notifying || !notifySentBy}
                        data-testid="confirm-notify-btn"
                      >
                        {notifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {notifying ? "Sending..." : "Send Notifications"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                {/* Config Button */}
                <Dialog open={showConfigDialog} onOpenChange={(open) => { setShowConfigDialog(open); if (open) fetchTodoLists(); }}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1" data-testid="config-btn">
                      <Settings className="w-4 h-4" />
                      <span className="hidden sm:inline">Config</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Post Intake Configuration</DialogTitle>
                      <DialogDescription>Manage document types and deadline categories</DialogDescription>
                    </DialogHeader>
                    <Tabs defaultValue="doctypes">
                      <TabsList className="w-full">
                        <TabsTrigger value="doctypes" className="flex-1">Document Types</TabsTrigger>
                        <TabsTrigger value="categories" className="flex-1">Categories</TabsTrigger>
                        <TabsTrigger value="todolist" className="flex-1">To-Do List</TabsTrigger>
                      </TabsList>
                      <TabsContent value="doctypes" className="space-y-3 mt-3">
                        {editDocTypes.map((dt, idx) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <Input
                              placeholder="Value (e.g. court_order)"
                              value={dt.value}
                              onChange={(e) => {
                                const updated = [...editDocTypes];
                                updated[idx] = { ...updated[idx], value: e.target.value };
                                setEditDocTypes(updated);
                              }}
                              className="flex-1"
                              data-testid={`config-doctype-value-${idx}`}
                            />
                            <Input
                              placeholder="Label (e.g. Court Order / Notice)"
                              value={dt.label}
                              onChange={(e) => {
                                const updated = [...editDocTypes];
                                updated[idx] = { ...updated[idx], label: e.target.value };
                                setEditDocTypes(updated);
                              }}
                              className="flex-1"
                              data-testid={`config-doctype-label-${idx}`}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditDocTypes(editDocTypes.filter((_, i) => i !== idx))}
                            >
                              <Trash2 className="w-4 h-4 text-status-red" />
                            </Button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={addDocType} className="gap-1">
                          <Plus className="w-3 h-3" /> Add Type
                        </Button>
                      </TabsContent>
                      <TabsContent value="categories" className="space-y-3 mt-3">
                        {editCategories.map((cat, idx) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <Input
                              placeholder="Value (e.g. court_date)"
                              value={cat.value}
                              onChange={(e) => {
                                const updated = [...editCategories];
                                updated[idx] = { ...updated[idx], value: e.target.value };
                                setEditCategories(updated);
                              }}
                              className="flex-1"
                              data-testid={`config-cat-value-${idx}`}
                            />
                            <Input
                              placeholder="Label (e.g. Court Date)"
                              value={cat.label}
                              onChange={(e) => {
                                const updated = [...editCategories];
                                updated[idx] = { ...updated[idx], label: e.target.value };
                                setEditCategories(updated);
                              }}
                              className="flex-1"
                              data-testid={`config-cat-label-${idx}`}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditCategories(editCategories.filter((_, i) => i !== idx))}
                            >
                              <Trash2 className="w-4 h-4 text-status-red" />
                            </Button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={addCategory} className="gap-1">
                          <Plus className="w-3 h-3" /> Add Category
                        </Button>
                      </TabsContent>
                      <TabsContent value="todolist" className="space-y-3 mt-3">
                        <p className="text-sm text-txt-muted">
                          Choose which Microsoft To Do list receives response tasks when "Response Required" is toggled during approval.
                        </p>
                        {loadingTodoLists ? (
                          <p className="text-sm text-slate-400">Loading To Do lists...</p>
                        ) : todoLists.length === 0 ? (
                          <div className="bg-status-amber-dim border border-amber-200 rounded-md p-3 text-sm text-status-amber" data-testid="todo-lists-empty">
                            No To Do lists found. Sign in with Microsoft to load your lists.
                          </div>
                        ) : (
                          <div className="space-y-2" data-testid="todo-lists-picker">
                            {todoLists.map(list => (
                              <label
                                key={list.id}
                                className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                  selectedTodoListId === list.id
                                    ? "border-teal bg-teal-dim"
                                    : "border-navy-border hover:bg-navy-hover/60"
                                }`}
                                data-testid={`todo-list-option-${list.id}`}
                              >
                                <input
                                  type="radio"
                                  name="todoList"
                                  checked={selectedTodoListId === list.id}
                                  onChange={() => setSelectedTodoListId(list.id)}
                                  className="accent-[#4f46e5]"
                                />
                                <div>
                                  <span className="font-medium text-sm text-white">{list.name}</span>
                                  {list.is_default && (
                                    <span className="ml-2 text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Default</span>
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>
                    <DialogFooter>
                      <Button onClick={handleSaveConfig} disabled={savingConfig} className="bg-teal hover:bg-teal/90" data-testid="save-config-btn">
                        {savingConfig ? "Saving..." : "Save Configuration"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Manual Create */}
                <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1" data-testid="manual-create-btn">
                      <Plus className="w-4 h-4" />
                      <span className="hidden sm:inline">Add Document</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Document Manually</DialogTitle>
                      <DialogDescription>Create a post intake record without SharePoint sync</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <div>
                        <label className="block text-sm font-medium text-txt mb-1">
                          Filename *
                        </label>
                        <Input
                          value={newFilename}
                          onChange={(e) => setNewFilename(e.target.value)}
                          placeholder="e.g. Court_Order_Smith_2025.pdf"
                          data-testid="manual-filename-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-txt mb-1">
                          Notes (optional)
                        </label>
                        <Input
                          value={newNotes}
                          onChange={(e) => setNewNotes(e.target.value)}
                          placeholder="Brief description..."
                          data-testid="manual-notes-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={handleManualCreate} disabled={creating || !newFilename.trim()} className="bg-teal hover:bg-teal/90" data-testid="manual-create-submit">
                        {creating ? "Creating..." : "Add to Queue"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Button
                  onClick={handleSync}
                  disabled={syncing}
                  className="gap-2 bg-teal hover:bg-teal/90"
                  data-testid="sync-sharepoint-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                  {syncing ? "Syncing..." : "Sync from SharePoint"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Tab Switcher */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full max-w-md">
            <TabsTrigger value="incoming" className="flex-1 gap-2" data-testid="tab-incoming">
              <Inbox className="w-4 h-4" />
              Incoming
            </TabsTrigger>
            <TabsTrigger value="outgoing" className="flex-1 gap-2" data-testid="tab-outgoing">
              <Upload className="w-4 h-4" />
              Outgoing
            </TabsTrigger>
          </TabsList>

          {/* ── Incoming Tab ────────────────────────────────────────── */}
          <TabsContent value="incoming" className="space-y-6 mt-4">
            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-4">
              <button
                onClick={() => setStatusFilter("pending")}
                className={`p-4 rounded-md border transition-colors ${
                  statusFilter === "pending"
                    ? "bg-status-amber-dim border-amber-300"
                    : "bg-white border-navy-border hover:border-amber-200"
                }`}
                data-testid="filter-pending"
              >
                <p className="text-2xl font-bold text-status-amber">{stats.pending}</p>
                <p className="text-sm text-slate-500">Pending Review</p>
              </button>
              <button
                onClick={() => setStatusFilter("claimed")}
                className={`p-4 rounded-md border transition-colors ${
                  statusFilter === "claimed"
                    ? "bg-blue-50 border-blue-300"
                    : "bg-white border-navy-border hover:border-blue-200"
                }`}
                data-testid="filter-claimed"
              >
                <p className="text-2xl font-bold text-blue-600">{stats.claimed}</p>
                <p className="text-sm text-slate-500">In Progress</p>
              </button>
              <button
                onClick={() => setStatusFilter("parked")}
                className={`p-4 rounded-md border transition-colors ${
                  statusFilter === "parked"
                    ? "bg-slate-100 border-slate-400"
                    : "bg-white border-navy-border hover:border-slate-300"
                }`}
                data-testid="filter-parked"
              >
                <p className="text-2xl font-bold text-txt-muted">{stats.parked}</p>
                <p className="text-sm text-slate-500">Parked</p>
              </button>
            </div>

            {/* Search */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by filename..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="claimed">Claimed</SelectItem>
                  <SelectItem value="parked">Parked</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchQueue}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {/* Queue List */}
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block w-8 h-8 border-2 border-teal border-t-transparent rounded-md animate-spin" />
                <p className="mt-4 text-slate-500">Loading queue...</p>
              </div>
            ) : filteredQueue.length === 0 ? (
              <div className="p-12 text-center bg-white rounded-md border border-dashed border-slate-300">
                <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No documents in queue</p>
                <p className="text-sm text-slate-400 mt-1">
                  {statusFilter !== "all" ? "Try changing the filter or " : ""}
                  Click "Sync from SharePoint" or "Add Document" to get started
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredQueue.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white border border-navy-border rounded-md p-4 hover:border-teal/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/post-intake/${item.id}`)}
                    data-testid={`post-item-${item.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-100 rounded-md flex items-center justify-center">
                          <FileText className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{item.filename}</p>
                          <p className="text-sm text-slate-500">
                            Received: {formatDate(item.received_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {item.ai_processed && (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 gap-1">
                            <Sparkles className="w-3 h-3" />
                            AI Analyzed
                          </Badge>
                        )}
                        {item.ai_suggestions?.deadlines?.length > 0 && (
                          <Badge variant="outline" className="bg-status-amber-dim text-status-amber border-amber-200">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {item.ai_suggestions.deadlines.length} deadline{item.ai_suggestions.deadlines.length !== 1 ? "s" : ""}
                          </Badge>
                        )}
                        {getStatusBadge(item.status)}
                        {item.last_notified_at && (
                          <Badge variant="outline" className="bg-teal-dim text-teal border-teal gap-1" title={`Notified: ${formatDate(item.last_notified_at)}`}>
                            <Send className="w-3 h-3" />
                            Notified
                          </Badge>
                        )}
                        {item.claimed_by && (
                          <span className="text-xs text-slate-400">
                            {item.claimed_by}
                          </span>
                        )}
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── Outgoing Tab ────────────────────────────────────────── */}
          <TabsContent value="outgoing" className="space-y-6 mt-4">
            {/* Header actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-md bg-status-amber-dim border border-amber-200">
                  <p className="text-xl font-bold text-status-amber">{scannedCounts.pending}</p>
                  <p className="text-xs text-slate-500">Pending Assign</p>
                </div>
                <div className="p-3 rounded-md bg-teal-dim border border-teal">
                  <p className="text-xl font-bold text-teal">{scannedCounts.filed}</p>
                  <p className="text-xs text-slate-500">Filed</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowManualUpload(v => !v)}
                  className="gap-1"
                  data-testid="toggle-manual-upload"
                >
                  <Upload className="w-4 h-4" />
                  {showManualUpload ? "Hide Manual Upload" : "Upload Manually"}
                </Button>
                <Button
                  onClick={handleOutgoingSync}
                  disabled={outSyncing}
                  className="gap-2 bg-teal hover:bg-teal/90"
                  data-testid="outgoing-sync-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${outSyncing ? "animate-spin" : ""}`} />
                  {outSyncing ? "Scanning..." : "Scan Outgoing Folder"}
                </Button>
              </div>
            </div>

            {/* Pending Scanned Items — need manual matter assignment */}
            {scannedLoading ? (
              <div className="p-12 text-center">
                <div className="inline-block w-8 h-8 border-2 border-teal border-t-transparent rounded-md animate-spin" />
                <p className="mt-4 text-slate-500">Loading scanned items...</p>
              </div>
            ) : (
              <>
                {/* Pending items */}
                {scannedItems.filter(i => i.status === "pending").length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-status-amber uppercase tracking-wider mb-3">
                      Pending Assignment ({scannedItems.filter(i => i.status === "pending").length})
                    </h3>
                    <div className="space-y-2">
                      {scannedItems.filter(i => i.status === "pending").map((item) => (
                        <div
                          key={item.id}
                          className="bg-white border border-amber-200 rounded-md p-4"
                          data-testid={`scanned-pending-${item.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-status-amber-dim rounded-md flex items-center justify-center">
                                <FileText className="w-5 h-5 text-status-amber" />
                              </div>
                              <div>
                                <p className="font-medium text-white">{item.filename}</p>
                                <p className="text-sm text-slate-500">
                                  Scanned: {formatDate(item.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="bg-status-amber-dim text-status-amber border-amber-200 gap-1">
                                <AlertCircle className="w-3 h-3" />
                                No matter matched
                              </Badge>
                              {assigningId === item.id ? (
                                <div className="relative w-64">
                                  <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                      autoFocus
                                      value={assignMatterSearch}
                                      onChange={(e) => handleAssignMatterSearch(e.target.value)}
                                      placeholder="Search matter..."
                                      className="pl-10 h-8 text-sm"
                                      data-testid={`assign-search-${item.id}`}
                                    />
                                    {assignSearching && (
                                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 animate-spin" />
                                    )}
                                  </div>
                                  <MatterSearchUnavailableNotice message={assignMatterError} />
                                  {assignMatterResults.length > 0 && (
                                    <div className="absolute z-20 w-full mt-1 bg-white border border-navy-border rounded-md shadow-lg max-h-40 overflow-y-auto">
                                      {assignMatterResults.map((folder) => (
                                        <button
                                          key={folder.id}
                                          onClick={() => handleAssignToMatter(item.id, folder.name)}
                                          className="w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--surface-inset)] transition-colors border-b border-slate-100 last:border-0"
                                        >
                                          <span className="font-medium">{folder.name}</span>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="absolute -right-8 top-0 h-8 w-8 p-0"
                                    onClick={() => { setAssigningId(null); setAssignMatterSearch(""); setAssignMatterResults([]); }}
                                  >
                                    <XCircle className="w-4 h-4 text-slate-400" />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1 border-teal text-teal hover:bg-teal/10"
                                  onClick={() => { setAssigningId(item.id); setAssignMatterSearch(""); setAssignMatterResults([]); }}
                                  data-testid={`assign-btn-${item.id}`}
                                >
                                  <Search className="w-3 h-3" />
                                  Assign to matter
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recently filed items */}
                {scannedItems.filter(i => i.status === "filed").length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-teal uppercase tracking-wider mb-3">
                      Recently Filed ({scannedItems.filter(i => i.status === "filed").length})
                    </h3>
                    <div className="space-y-2">
                      {scannedItems.filter(i => i.status === "filed").map((item) => (
                        <div
                          key={item.id}
                          className="bg-white border border-navy-border rounded-md p-4"
                          data-testid={`scanned-filed-${item.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-teal-dim rounded-md flex items-center justify-center">
                                <CheckCircle2 className="w-5 h-5 text-teal" />
                              </div>
                              <div>
                                <p className="font-medium text-white">{item.filename}</p>
                                <p className="text-sm text-slate-500">
                                  {item.matter_ref} &middot; Scanned: {formatDate(item.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="bg-teal-dim text-teal border-teal gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Filed
                              </Badge>
                              {item.filed_sharepoint_url && (
                                <a
                                  href={item.filed_sharepoint_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-teal hover:underline"
                                >
                                  Open
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {scannedItems.length === 0 && (
                  <div className="p-12 text-center bg-white rounded-md border border-dashed border-slate-300">
                    <Send className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No scanned outgoing documents yet</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Click "Scan Outgoing Folder" to check for new files in OUTGOING POST
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Collapsible Manual Upload Form */}
            {showManualUpload && (
              <div className="bg-white border border-navy-border rounded-md p-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-teal" />
                  Upload Manually
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Matter Search */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-txt mb-1">
                      Matter Reference <span className="text-status-red">*</span>
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={outMatterSearch}
                        onChange={(e) => handleMatterSearch(e.target.value)}
                        placeholder="Search for a matter..."
                        className="pl-10"
                        data-testid="outgoing-matter-search"
                      />
                      {outSearching && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                      )}
                    </div>
                    <MatterSearchUnavailableNotice message={outMatterError} />
                    {outMatterResults.length > 0 && !outMatterRef && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-navy-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {outMatterResults.map((folder) => (
                          <button
                            key={folder.id}
                            onClick={() => selectMatter(folder)}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-[color:var(--surface-inset)] transition-colors border-b border-slate-100 last:border-0"
                            data-testid={`outgoing-matter-option-${folder.id}`}
                          >
                            <span className="font-medium">{folder.name}</span>
                            <span className="text-slate-400 ml-2 text-xs">{folder.childCount} items</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Document Type */}
                  <div>
                    <label className="block text-sm font-medium text-txt mb-1">
                      Document Type <span className="text-status-red">*</span>
                    </label>
                    <Select value={outDocType} onValueChange={setOutDocType}>
                      <SelectTrigger data-testid="outgoing-doc-type">
                        <SelectValue placeholder="Select type..." />
                      </SelectTrigger>
                      <SelectContent>
                        {OUTGOING_DOC_TYPES.map((dt) => (
                          <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-txt mb-1">
                      Description <span className="text-slate-400 text-xs">(optional)</span>
                    </label>
                    <Input
                      value={outDescription}
                      onChange={(e) => setOutDescription(e.target.value)}
                      placeholder="e.g. Letter to Land Registry re Title"
                      data-testid="outgoing-description"
                    />
                  </div>

                  {/* Date Sent */}
                  <div>
                    <label className="block text-sm font-medium text-txt mb-1">
                      Date Sent
                    </label>
                    <Input
                      type="date"
                      value={outDateSent}
                      onChange={(e) => setOutDateSent(e.target.value)}
                      data-testid="outgoing-date-sent"
                    />
                  </div>

                  {/* File Upload */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-txt mb-1">
                      File <span className="text-status-red">*</span>
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                      onChange={(e) => setOutFile(e.target.files?.[0] || null)}
                      className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-teal/10 file:text-teal hover:file:bg-teal/20"
                      data-testid="outgoing-file-input"
                    />
                    {outFile && (
                      <p className="text-xs text-slate-400 mt-1">{outFile.name} ({(outFile.size / 1024).toFixed(0)} KB)</p>
                    )}
                  </div>
                </div>

                {/* Submit */}
                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={handleOutgoingSubmit}
                    disabled={outUploading || !outFile || !outDocType || !(outMatterRef || outMatterSearch.trim())}
                    className="gap-2 bg-teal hover:bg-teal/90"
                    data-testid="outgoing-submit"
                  >
                    {outUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Upload to SharePoint
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Recent Manual Uploads */}
            {outRecentItems.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-txt-muted uppercase tracking-wider mb-3">
                  Recent Manual Uploads
                </h3>
                <div className="space-y-2">
                  {outRecentItems.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white border border-navy-border rounded-md p-4"
                      data-testid={`outgoing-item-${item.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center">
                            <Send className="w-5 h-5 text-blue-500" />
                          </div>
                          <div>
                            <p className="font-medium text-white">
                              {item.original_filename}
                            </p>
                            <p className="text-sm text-slate-500">
                              {item.matter_ref} &middot; {OUTGOING_DOC_TYPES.find(t => t.value === item.document_type)?.label || item.document_type}
                              {item.description ? ` — ${item.description}` : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-400">
                            Sent: {item.date_sent}
                          </span>
                          {item.sharepoint_url ? (
                            <Badge variant="outline" className="bg-teal-dim text-teal border-teal gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Filed
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-status-amber-dim text-status-amber border-amber-200 gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Pending
                            </Badge>
                          )}
                          {item.sharepoint_url && (
                            <a
                              href={item.sharepoint_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs text-teal hover:underline"
                            >
                              Open
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
